import {
  BaseRecord,
  CrudOperators,
  HttpError,
  LogicalFilter,
  useTable as useRefineTable,
  useTableProps,
  useTableReturnType,
} from '@refinedev/core';
import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  PaginationState,
  SortingState,
  Table,
  TableOptions,
  useReactTable as useTanstackReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import React, {useMemo} from 'react';
import useRefineDataTable from './useRefineDataTable';

export type ConvertDataOptions = {
  refine_to_tanstack?: (data: any) => any;
  tanstack_to_refine?: (data: any) => any;
};
/**
 * Custom hook to create a data table with advanced features such as pagination, sorting, row selection, and column visibility.
 *
 * @template TData - The type of data records.
 * @template TError - The type of error that can occur.
 * @param {ColumnDef<TData>[]} columns - The column definitions for the table.
 * @param {useTableProps<TData, TError, TData>} refineProps - The properties for the table.
 * @returns {Table<TData>} - The configured table instance.
 *
 * @example
 * const columns = [
 *   { accessorKey: 'id', header: 'ID' },
 *   { accessorKey: 'name', header: 'Name' },
 * ];
 * const props = { resource: 'users', initialPageSize: 10 };
 * const table = useDataTable(columns, props);
 */
const useRefineTanstackDataTable = <TData extends BaseRecord, TError extends HttpError>(
  refineProps: useTableProps<TData, TError, TData>,
  tanskstackProps: Pick<TableOptions<TData>, 'columns'> & Partial<TableOptions<TData>>,
  convertDataOptions?: ConvertDataOptions,
): {refineTable: useTableReturnType; tanstackTable: Table<TData>} => {
  const {columns, ...rest_of_tanstackProps} = tanskstackProps;

  const {
    refineTable,
    data: rData,
    sorting,
    columnVisibility,
    rowSelection,
    pagination,
    globalFilter,
    total,
    onPaginationChange,
    onSortingChange,
    onGlobalFilterChange,
    setRowSelection,
    columnFilters,
    onColumnFiltersChange,
    setColumnVisibility,
  } = useRefineDataTable({errorNotification: false, ...refineProps}, columns);

  const data = useMemo(() => {
    if (convertDataOptions?.refine_to_tanstack) {
      return convertDataOptions.refine_to_tanstack(rData);
    }
    return rData;
  }, [rData]);

  const tanstackTable = useTanstackReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination,
      globalFilter,
    },
    enableRowSelection: true,
    pageCount: Math.ceil(total / refineTable.pageSize),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,

    onPaginationChange,
    onSortingChange,
    onGlobalFilterChange,
    onRowSelectionChange: setRowSelection,

    onColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    // models
    getCoreRowModel: getCoreRowModel(),
    // getFilteredRowModel: getFilteredRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    // getSortedRowModel: getSortedRowModel(),
    // getFacetedRowModel: getFacetedRowModel(),
    // getFacetedUniqueValues: getFacetedUniqueValues(),
    ...rest_of_tanstackProps,
  });

  return {refineTable, tanstackTable};
};

export default useRefineTanstackDataTable;
